import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { PublicUser } from "@book2class/shared";
import { api, ApiError } from "./api";

type PendingConsent = { id: string; type: "terms" | "privacy"; version: string; title: string };

type AuthState = {
  user: PublicUser | null;
  loading: boolean;
  pendingConsents: PendingConsent[];
  refresh: () => Promise<void>;
  setUser: (u: PublicUser | null) => void;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

/** Aplica as cores da instituição às variáveis de tema. */
function applyBranding(user: PublicUser | null) {
  const root = document.documentElement;
  const b = user?.institution.branding ?? {};
  if (b.primaryColor) root.style.setProperty("--brand", b.primaryColor);
  else root.style.removeProperty("--brand");
  if (b.accentColor) root.style.setProperty("--accent", b.accentColor);
  else root.style.removeProperty("--accent");
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUserState] = useState<PublicUser | null>(null);
  const [pendingConsents, setPending] = useState<PendingConsent[]>([]);
  const [loading, setLoading] = useState(true);

  const setUser = useCallback((u: PublicUser | null) => {
    setUserState(u);
    applyBranding(u);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const me = await api.get<{ user: PublicUser; pendingConsents: PendingConsent[] }>("/auth/me");
      setUser(me.user);
      setPending(me.pendingConsents);
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 401) console.error(err);
      setUser(null);
      setPending([]);
    } finally {
      setLoading(false);
    }
  }, [setUser]);

  const logout = useCallback(async () => {
    await api.post("/auth/logout").catch(() => undefined);
    // Apaga as aulas e dados guardados para uso offline (dispositivos compartilhados).
    navigator.serviceWorker?.controller?.postMessage("logout");
    await window.caches
      ?.keys()
      .then((keys) => Promise.all(keys.filter((k) => k.startsWith("b2c-data")).map((k) => caches.delete(k))))
      .catch(() => undefined);
    setUser(null);
    setPending([]);
  }, [setUser]);

  useEffect(() => {
    // Carga inicial da sessão: o estado só muda após a resposta da API.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({ user, loading, pendingConsents, refresh, setUser, logout }),
    [user, loading, pendingConsents, refresh, setUser, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth precisa estar dentro de AuthProvider");
  return ctx;
}

export const isTeacher = (u: PublicUser | null) => Boolean(u && (u.isPlatformAdmin || u.role !== "student"));
export const homeFor = (u: PublicUser) => (isTeacher(u) ? "/professor/cursos" : "/aluno");
